<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFetch, refDebounced } from '@vueuse/core'

import Input from '@/components/ui/input_with_submit_icon.vue'

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  },
  defaultSearchModel: {
    type: String,
    default: ''
  }
})

const model = defineModel()

// Stores

// Variables
const { t } = useI18n()
const expandDropdown = ref(false)
const searchModel = ref(props.defaultSearchModel)
const debouncedSearch = refDebounced(searchModel, 600)

// LocationQuery
const searchUrl = computed(() => {
  const url = new URL('https://nominatim.openstreetmap.org/search')
  url.searchParams.append('format', 'json')
  url.searchParams.append('featureType', 'city')
  url.searchParams.append('limit', '10')
  url.searchParams.append('q', debouncedSearch.value)
  url.searchParams.append('addressdetails', '1')
  url.searchParams.append('countrycodes', 'us')
  return url.toString()
})
const {
  data: searchData,
  isFetching: searchIsFetching,
  error: searchError
} = useFetch(searchUrl, { refetch: true }).json()

const searchResults = computed(() => {
  const types = ['municipality', 'city', 'town', 'village', 'city_district', 'hamlet']
  return searchData.value
    .filter(place => types.some(x => Object.keys(place.address).includes(x)))
    .map((result) => {
      const state_display = result.address.state

      const foundProp = types.find(prop => result.address.hasOwnProperty(prop))
      const city_display = foundProp ? result.address[foundProp] : undefined

      return { ...result, full_name: `${city_display}, ${state_display}` }
    })
})

function openResults() {
  expandDropdown.value = document.querySelector(`.search-container`)
}

onMounted(async () => {
  document.addEventListener('click', closeExpandOnOutsideClick)
})

onUnmounted(() => {
  document.removeEventListener('click', closeExpandOnOutsideClick)
})

function closeExpandOnOutsideClick(event) {
  if (expandDropdown.value && !expandDropdown.value.contains(event.target)) {
    expandDropdown.value = false
  }
}

function setResult(result) {
  model.value = result
  expandDropdown.value = false
  searchModel.value = ''
}

function clearSearch() {
  searchModel.value = ''
  expandDropdown.value = false
}

defineExpose({
  clearSearch
})

</script>

<template>
  <div class="search-container">
    <Input :placeholder="t('service_providers.search_filters.search_placeholder')"
           v-model="searchModel"
           @focus="openResults()"
           :disabled="disabled"
           :showSubmit="false" />

    <div class="search-results-container" v-if="expandDropdown && searchModel.length">
      <div class="" v-if="searchIsFetching">{{ t('service_providers.search_filters.loading') }}</div>
      <div class="search-results shadow-xl" v-if="searchResults.length">
        <ul v-for="result in searchResults" :key="result.place_id">
          <li class="px-4 py-2 hover:bg-gray-200 hover:cursor-pointer" @click="setResult(result)">
            {{ result.full_name }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/request_a_quote/request_a_quote';

.search-container {
  position: relative;
  background-color: #fbf5e9;

  .search-results-container {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    z-index: 10;
    background-color: white;
    border: 1px solid #fbf5e9;

    .search-results {
      li {
        list-style: none;
      }
    }
  }
}
</style>